import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ProgramOverview } from '@/components/ProgramOverview';
import { setDatadogGlobalContext, trackEvent } from '@/modules/analytics';
import { highestRecommendedProgramUuid, programMetaInfoKey } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, LoadingScreen, Screen, ScreenHeading } from '@/modules/ui';

/**
 * PRIMARY RECOMMENDATION DETAIL — shown first (instead of the full list) when
 * the prescribe-programs response says `goStraightToDetailsPage` (Sparta's
 * prescribed / hard-recommended flows). Presents the recommended program;
 * "Join" enrolls. "See other options" shows only when the response allows
 * additional choices (`additionalChoicesAllowed`).
 *
 * When the `autoJoinProgram` flag is on AND the program is prescribed (no
 * additional choices), the screen auto-enrolls on mount and shows a loading
 * state instead of the manual Join — Sparta's loadProgramSuccessEpic auto-join.
 */
export function PrimaryRecommendationProgramDetailScreen() {
  const t = useT();
  const {
    prescribedPrograms,
    selectedProgramUuid,
    setSelectedProgram,
    setCurrentRoute,
    enrollInProgram,
    user,
  } = useRulesEngine();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const autoJoinAttempted = useRef(false);

  // Which program this screen presents differs by flow (Sparta's
  // loadProgramSuccessEpic): a HARD recommendation (additionalChoicesAllowed)
  // shows the highest-recommendation-score program; a PRESCRIBED one
  // (no additional choices) shows the single prescribed program — the first.
  const programs = prescribedPrograms?.programs ?? [];
  const targetUuid = prescribedPrograms?.additionalChoicesAllowed
    ? (highestRecommendedProgramUuid(prescribedPrograms) ?? programs[0]?.uuid)
    : programs[0]?.uuid;
  const program = programs.find((p) => p.uuid === (selectedProgramUuid ?? targetUuid));

  // The prescribed program carries its own coaching method; present the matching
  // detail variant (a prescribed group program shows the group copy).
  const prescribedMethod = program
    ? prescribedPrograms?.programMetaInfo[programMetaInfoKey(program.uuid)]
        ?.prescribedCoachingMethod
    : undefined;
  const modality = prescribedMethod === 'group' ? 'group' : 'individual';

  // Prescribed (no "See other options") → enroll without a manual press. Mirrors
  // Sparta's Prescribed = goStraightToDetailsPage (this screen) &&
  // !additionalChoicesAllowed. Auto-join is Split-gated in Sparta
  // (`sparta_auto_join_program`); onboarding has no Split, so it's enabled by
  // default here — see featureFlags note.
  const isPrescribed = prescribedPrograms?.additionalChoicesAllowed === false;
  const autoJoin = isPrescribed;

  const onJoin = useCallback(async () => {
    if (!program) return;
    trackEvent('program-join-hard-rec-or-prescribe', {
      uuid: user?.uuid,
      programUrn: program.urn,
      programTitle: program.title,
      isAlreadyEnrolled: false,
    });
    void setDatadogGlobalContext({ selectedProgram: program.title });
    setSubmitError(false);
    try {
      setIsSubmitting(true);
      await enrollInProgram(program.uuid);
    } catch {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  }, [program, enrollInProgram, user?.uuid]);

  const onSeeOtherOptions = useCallback(() => {
    trackEvent('program-overview-expand-selection', {
      uuid: user?.uuid,
      fromProgramUrn: program?.urn,
      recommendationContext: 'hard_recommend',
    });
    setSelectedProgram(undefined);
    setCurrentRoute('/step/program-matching');
  }, [program?.urn, user?.uuid, setSelectedProgram, setCurrentRoute]);

  // The resolver routes here without a user selection, so seed the rules engine
  // with the program above (keeps the selection consistent for the manual path).
  useEffect(() => {
    if (!selectedProgramUuid && program) setSelectedProgram(program.uuid);
  }, [selectedProgramUuid, program, setSelectedProgram]);

  // Auto-join: enroll once on mount. On failure, fall through to the manual
  // screen (submitError) so the user can retry / see other options.
  useEffect(() => {
    if (!autoJoin || autoJoinAttempted.current || !program || submitError) return;
    autoJoinAttempted.current = true;
    void onJoin();
  }, [autoJoin, program, submitError, onJoin]);

  if (!program) return <LoadingScreen />;
  // Auto-joining a prescribed program: show loading instead of the detail so the
  // manual screen never flashes — the driver navigates away once enrollment lands.
  if (autoJoin && !submitError) return <LoadingScreen />;

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <VStack space='md'>
          <CtaButton
            isBusy={isSubmitting}
            isDisabled={isSubmitting}
            onPress={onJoin}
            testID='primary-recommendation-join'
          >
            <T _str='Join program' />
          </CtaButton>
          {prescribedPrograms?.additionalChoicesAllowed ? (
            <CtaButton
              type='secondary'
              onPress={onSeeOtherOptions}
              testID='primary-recommendation-see-other-options'
            >
              <T _str='See other options' />
            </CtaButton>
          ) : null}
        </VStack>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading title={t('We found a program that’s a great fit for you!')} />
        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong joining the program. Please try again.' />
          </Text>
        ) : null}
        <ProgramOverview program={program} modality={modality} />
      </VStack>
    </Screen>
  );
}
